import React from 'react';
import { createPortal } from 'react-dom';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import './SignatureModal.css';

export type SignatureModalMethod = 'typed_name' | 'e_signature';

export type SignatureModalValue = {
  method: SignatureModalMethod;
  signerName: string | null;
  signedAt: string;
  imageDataUrl: string | null;
  imageMimeType: 'image/png' | null;
  signatureData: Record<string, unknown>;
};

interface SignatureModalProps {
  open: boolean;
  /** Use when the shared signature dialog is opened from another modal. */
  overlayClassName?: string;
  title?: string;
  description?: React.ReactNode;
  acceptanceStatement?: React.ReactNode;
  acceptanceDetail?: React.ReactNode;
  acceptanceAriaLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  defaultMethod?: SignatureModalMethod;
  defaultSignerName?: string;
  /** Opt in only when the authenticated user is signing as themselves. */
  savedSignatureDataUrl?: string | null;
  typedLabel?: string;
  typedPlaceholder?: string;
  typedPreviewEmpty?: string;
  typedMethodLabel?: string;
  drawMethodLabel?: string;
  requireSignerNameForDrawn?: boolean;
  minTypedLength?: number;
  uppercaseTyped?: boolean;
  leadingContent?: React.ReactNode;
  extraOption?: React.ReactNode;
  extraConfirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: (value: SignatureModalValue) => void;
}

type SignaturePoint = {
  x: number;
  y: number;
};

function getCanvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 2.6;
  ctx.strokeStyle = '#111827';
  return ctx;
}

function resetCanvasTransform(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(1, rect.width || 640);
  const height = Math.max(1, rect.height || 190);
  const dpr = Math.max(1, window.devicePixelRatio || 1);

  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 2.6;
  ctx.strokeStyle = '#111827';
}

function clearCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
}

export default function SignatureModal({
  open,
  overlayClassName,
  title = 'Capture signature',
  description,
  acceptanceStatement,
  acceptanceDetail,
  acceptanceAriaLabel = 'Accept signature terms',
  confirmLabel = 'Save signature',
  cancelLabel = 'Cancel',
  busy = false,
  defaultMethod = 'typed_name',
  defaultSignerName = '',
  savedSignatureDataUrl = null,
  typedLabel = 'Full name',
  typedPlaceholder = 'Enter your full legal name',
  typedPreviewEmpty = 'Your typed signature will preview here',
  typedMethodLabel = 'Type name',
  drawMethodLabel = 'Draw signature',
  requireSignerNameForDrawn = false,
  minTypedLength = 2,
  uppercaseTyped = false,
  leadingContent,
  extraOption,
  extraConfirmDisabled = false,
  onCancel,
  onConfirm,
}: SignatureModalProps) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const acceptanceId = React.useId();
  const acceptanceTextId = React.useId();
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const contextRef = React.useRef<CanvasRenderingContext2D | null>(null);
  const typedInputRef = React.useRef<HTMLInputElement | null>(null);
  const lastPointRef = React.useRef<SignaturePoint | null>(null);
  const drawingRef = React.useRef(false);
  const hasDrawingRef = React.useRef(false);
  const canvasSizeRef = React.useRef({ width: 0, height: 0 });
  const [method, setMethod] = React.useState<SignatureModalMethod | 'saved'>(savedSignatureDataUrl ? 'saved' : defaultMethod);
  const [typedName, setTypedName] = React.useState(defaultSignerName);
  const [hasDrawing, setHasDrawing] = React.useState(false);
  const [accepted, setAccepted] = React.useState(!acceptanceStatement);
  const requiresAcceptance = Boolean(acceptanceStatement);

  useBodyScrollLock(open);

  // Seed the form once per opening — never on a later prop change. Callers
  // recompute `defaultSignerName` from state the signer edits inside this modal,
  // and `acceptanceStatement` can appear or disappear while it is open (the
  // signer page derives it from the "complete every field" toggle). Re-running
  // this on those changes reset the method back to typed and discarded a
  // half-finished drawing mid-signature.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    setMethod(savedSignatureDataUrl ? 'saved' : defaultMethod);
    setTypedName(defaultSignerName);
    setHasDrawing(false);
    drawingRef.current = false;
    lastPointRef.current = null;
  }, [defaultMethod, defaultSignerName, open, savedSignatureDataUrl]);

  // Acceptance is deliberately still re-armed whenever the requirement changes:
  // if this modal becomes one that completes the document, the signer must tick
  // the acknowledgment themselves rather than inherit an earlier `true`.
  React.useEffect(() => {
    if (!open) return;
    setAccepted(!requiresAcceptance);
  }, [open, requiresAcceptance]);

  React.useEffect(() => {
    if (!open || method !== 'e_signature') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = getCanvasContext(canvas);
    if (!ctx) return;
    contextRef.current = ctx;
    resetCanvasTransform(canvas, ctx);
    clearCanvas(canvas, ctx);
    const initialRect = canvas.getBoundingClientRect();
    canvasSizeRef.current = {
      width: Math.round(initialRect.width),
      height: Math.round(initialRect.height),
    };

    // Mobile browsers fire `resize` when the URL bar shows or hides during a
    // scroll. Resizing the backing store wipes the canvas, so a signer who drew
    // and then scrolled lost their signature. Ignore resizes that do not change
    // the drawing surface, and redraw the existing mark across the ones that do.
    function handleResize() {
      const target = canvasRef.current;
      const context = contextRef.current;
      if (!target || !context) return;
      const rect = target.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      if (
        width === canvasSizeRef.current.width
        && height === canvasSizeRef.current.height
      ) {
        return;
      }
      const snapshot = hasDrawingRef.current ? target.toDataURL('image/png') : null;
      canvasSizeRef.current = { width, height };
      resetCanvasTransform(target, context);
      clearCanvas(target, context);
      if (!snapshot) {
        setHasDrawing(false);
        return;
      }
      const restored = new Image();
      restored.onload = () => {
        const liveContext = contextRef.current;
        if (liveContext) liveContext.drawImage(restored, 0, 0, width, height);
      };
      restored.src = snapshot;
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [method, open]);

  // Mirrors `hasDrawing` for the resize handler, whose listener closure is
  // registered once per open and would otherwise read a stale value.
  React.useEffect(() => {
    hasDrawingRef.current = hasDrawing;
  }, [hasDrawing]);

  React.useEffect(() => {
    if (!open) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) {
        onCancel();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [busy, onCancel, open]);

  React.useEffect(() => {
    if (!open || method !== 'typed_name') return;
    const id = window.setTimeout(() => typedInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [method, open]);

  const getPoint = React.useCallback((event: React.PointerEvent<HTMLCanvasElement>): SignaturePoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  const drawPoint = React.useCallback((from: SignaturePoint, to: SignaturePoint) => {
    const ctx = contextRef.current;
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }, []);

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (busy) return;
    event.preventDefault();
    const point = getPoint(event);
    drawingRef.current = true;
    lastPointRef.current = point;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drawPoint(point, { x: point.x + 0.1, y: point.y + 0.1 });
    setHasDrawing(true);
  }, [busy, drawPoint, getPoint]);

  const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || busy) return;
    event.preventDefault();
    const nextPoint = getPoint(event);
    const previousPoint = lastPointRef.current ?? nextPoint;
    drawPoint(previousPoint, nextPoint);
    lastPointRef.current = nextPoint;
  }, [busy, drawPoint, getPoint]);

  const finishDrawing = React.useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    drawingRef.current = false;
    lastPointRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const handleClearDrawing = React.useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    if (!canvas || !ctx || busy) return;
    clearCanvas(canvas, ctx);
    setHasDrawing(false);
  }, [busy]);

  const trimmedName = typedName.trim();
  const canConfirm = accepted && !busy && !extraConfirmDisabled && (
    method === 'typed_name'
      ? trimmedName.length >= minTypedLength
      : (method === 'saved' ? Boolean(savedSignatureDataUrl) : hasDrawing) && (!requireSignerNameForDrawn || trimmedName.length >= minTypedLength)
  );

  const selectTypedNameMethod = React.useCallback(() => {
    if (busy) return;
    setMethod('typed_name');
  }, [busy]);

  const selectDrawSignatureMethod = React.useCallback(() => {
    if (busy) return;
    setMethod('e_signature');
  }, [busy]);

  const handleTypedNameMethodPointerDown = React.useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    event.preventDefault();
    selectTypedNameMethod();
  }, [selectTypedNameMethod]);

  const handleDrawSignatureMethodPointerDown = React.useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    event.preventDefault();
    selectDrawSignatureMethod();
  }, [selectDrawSignatureMethod]);

  const handleConfirm = React.useCallback(() => {
    if (!canConfirm) return;
    const signedAt = new Date().toISOString();
    if (method === 'typed_name') {
      onConfirm({
        method,
        signerName: trimmedName,
        signedAt,
        imageDataUrl: null,
        imageMimeType: null,
        signatureData: {
          typed_name: trimmedName,
          signed_at: signedAt,
          input_method: 'typed',
        },
      });
      return;
    }

    const imageDataUrl = method === 'saved' ? savedSignatureDataUrl : canvasRef.current?.toDataURL('image/png') ?? null;
    onConfirm({
      method: 'e_signature',
      signerName: trimmedName || null,
      signedAt,
      imageDataUrl,
      imageMimeType: 'image/png',
      signatureData: {
        drawn_signature: imageDataUrl,
        drawn_signature_mime_type: 'image/png',
        ...(trimmedName ? { signer_name: trimmedName } : {}),
        signed_at: signedAt,
        input_method: method === 'saved' ? 'saved_profile' : 'drawn',
      },
    });
  }, [canConfirm, method, onConfirm, savedSignatureDataUrl, trimmedName]);

  if (!open || typeof document === 'undefined') return null;

  const dialog = (
    <div
      className={`modal-overlay modal-overlay--solid${overlayClassName ? ` ${overlayClassName}` : ''}`}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onCancel();
        }
      }}
    >
      <div
        className="modal signature-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        {/* No close affordance in the header: Cancel in the footer, Escape, and
            the overlay all dismiss, and on mobile the X competed with the
            signing task for the top-right thumb zone. */}
        <div className="modal-header">
          <div className="signature-modal__intro" style={{ minWidth: 0 }}>
            <h2 id={titleId}>{title}</h2>
            {description ? (
              <div id={descriptionId} className="text-sm fg-muted">
                {description}
              </div>
            ) : null}
          </div>
        </div>
        <div className="modal-body signature-modal__body">
          {leadingContent ? (
            <div className="signature-modal__leading">{leadingContent}</div>
          ) : null}
          <div className={`signature-modal__methods${savedSignatureDataUrl ? " signature-modal__methods--saved" : ""}`} role="tablist" aria-label="Signature method">
            {savedSignatureDataUrl ? <button type="button" className="signature-modal__method"
              data-active={method === 'saved'} role="tab" aria-selected={method === 'saved'}
              onClick={() => setMethod('saved')} disabled={busy}>Saved signature</button> : null}
            <button
              type="button"
              className="signature-modal__method"
              data-active={method === 'typed_name'}
              role="tab"
              aria-selected={method === 'typed_name'}
              onPointerDown={handleTypedNameMethodPointerDown}
              onClick={selectTypedNameMethod}
              disabled={busy}
            >
              {typedMethodLabel}
            </button>
            <button
              type="button"
              className="signature-modal__method"
              data-active={method === 'e_signature'}
              role="tab"
              aria-selected={method === 'e_signature'}
              onPointerDown={handleDrawSignatureMethodPointerDown}
              onClick={selectDrawSignatureMethod}
              disabled={busy}
            >
              {drawMethodLabel}
            </button>
          </div>

          {method === 'typed_name' ? (
            <div className="signature-modal__field">
              <label className="text-sm font-semibold" htmlFor={`${titleId}-typed-name`}>
                {typedLabel}
              </label>
              <input
                ref={typedInputRef}
                id={`${titleId}-typed-name`}
                className="control"
                type="text"
                value={typedName}
                onChange={(event) => setTypedName(
                  uppercaseTyped ? event.target.value.toUpperCase() : event.target.value,
                )}
                autoComplete={uppercaseTyped ? 'off' : 'name'}
                placeholder={typedPlaceholder}
                disabled={busy}
              />
              <div
                className="signature-modal__typed-preview"
                data-empty={trimmedName ? 'false' : 'true'}
                aria-label="Typed signature preview"
              >
                {trimmedName || typedPreviewEmpty}
              </div>
            </div>
          ) : (
            <div className="signature-modal__draw-area">
              {requireSignerNameForDrawn ? (
                <label className="signature-modal__field">
                  <span className="text-sm font-semibold">Signer name</span>
                  <input
                    className="control"
                    type="text"
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                    autoComplete="name"
                    placeholder="Enter the name of the person signing"
                    disabled={busy}
                  />
                </label>
              ) : null}
              {method === 'saved' ? <div className="signature-modal__saved-preview">
                <img src={savedSignatureDataUrl ?? ''} alt="Your saved profile signature" />
                <p className="text-sm fg-muted">Your signature on file. Accept the statement below to use it for this certification.</p>
              </div> : <>
              <div className="signature-modal__canvas-shell">
                <canvas
                  ref={canvasRef}
                  className="signature-modal__canvas"
                  aria-label="Signature drawing pad"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={finishDrawing}
                  onPointerCancel={finishDrawing}
                  onPointerLeave={finishDrawing}
                />
                <div className="signature-modal__baseline" />
                {/* The pad must swallow touch gestures to draw, so it cannot
                    also scroll. Say where scrolling does work. */}
                <div className="signature-modal__draw-hint">
                  Sign with a finger, pen, or mouse. Scroll outside this box.
                </div>
              </div>
              <div className="signature-modal__draw-actions">
                <button
                  type="button"
                  className="btn sm"
                  onClick={handleClearDrawing}
                  disabled={busy || !hasDrawing}
                >
                  Clear
                </button>
              </div>
              </>}
            </div>
          )}

          {extraOption ? (
            <div className="signature-modal__extra-option">{extraOption}</div>
          ) : null}

          {acceptanceStatement ? (
            <div className="signature-modal__acceptance text-sm">
              <input
                id={acceptanceId}
                type="checkbox"
                aria-label={acceptanceAriaLabel}
                aria-describedby={acceptanceTextId}
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
                disabled={busy}
              />
              <div className="signature-modal__acceptance-copy">
                <div id={acceptanceTextId}>{acceptanceStatement}</div>
                {acceptanceDetail ? (
                  <div className="signature-modal__acceptance-detail">
                    {acceptanceDetail}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {busy ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
